import type { Getter } from 'vuex';
import type { Control } from '@@/packages/shared/types/models/control';
import type { Domain } from '@@/packages/shared/types/models/domain';
import type { State as RootState } from '@/store/useStore';
import type { AssessmentState } from './mutations';

// Get object of domainID mapped to number of answers
const numAnswersByDomain: Getter<AssessmentState, RootState> = (
  state,
  _getters,
  rootState,
  rootGetters,
) => {
  const controlsByDomain = rootGetters['framework/controlsByDomain'] as Record<string, string[]>;
  const { frameworkRequiredControlIDs } = rootState.framework;
  return Object.fromEntries(
    Object.entries(controlsByDomain).map(([domainID, controls]) => [
      domainID,
      controls.reduce((i, c) => {
        if (!frameworkRequiredControlIDs.includes(c)) return i;
        const a = state.controlAnswers[c]
          ? state.controlAnswers[c].answer || state.controlAnswers[c].notApplicable
          : null;
        return a ? i + 1 : i;
      }, 0),
    ]),
  );
};

// Get percentage of controls that have been answered, by domain
// Ignores deprecated controls in percentage
const percentageDomainAnswered: Getter<AssessmentState, RootState> =
  (_state, getters, rootState, rootGetters) => (domainID: string) => {
    const controlsByDomain = rootGetters['framework/controlsByDomain'] as Record<string, string[]>;
    const { frameworkRequiredControlIDs } = rootState.framework;
    const controls =
      controlsByDomain[domainID]?.filter((control) => frameworkRequiredControlIDs.includes(control))
        .length || 0;
    const answers = getters.numAnswersByDomain[domainID];
    return Math.floor((answers / controls) * 100);
  };

const isScopingComplete: Getter<AssessmentState, RootState> = (state) =>
  state.status ? state.status.scopingComplete : false;

const isAssessmentComplete: Getter<AssessmentState, RootState> = (state) =>
  state.status ? state.status.initialAssessmentComplete : false;

// Get sorted deprecated controls that have been answered.
// Returns a list of controlIDs.
// TODO: (30 minutes) what is the difference between this and the profile store
// getter of the same name?
const sortedControlAnswersDeprecated: Getter<AssessmentState, RootState> = (
  state,
  _getters,
  _rootState,
  rootGetters,
) => {
  const controlAnswersIDs = Object.keys(state.controlAnswers);
  const sortedControlsDeprecated = (
    rootGetters['framework/sortedControlsDeprecated'] as Control[]
  ).reduce((res, controlObj) => {
    res.push(controlObj.controlID);
    return res;
  }, [] as string[]);
  return sortedControlsDeprecated.filter((value) => controlAnswersIDs.includes(value));
};

// Moved to composable but still kept because another getter uses it still.
const controlsRequiringAttention: Getter<AssessmentState, RootState> = (
  state,
  getters,
  _rootState,
  rootGetters,
) => {
  const sortedControls = rootGetters['framework/sortedControls'] as Control[];
  const answers = state.controlAnswers;
  const outOfScope = getters.outOfScopeDeprecatedDoNotUseMe;

  // Only controls that changed meaning: domains with unanswered controls already reach
  // sortedIncompleteDomains through percentageDomainAnswered.
  return sortedControls
    .filter((control) => {
      // Ignore deprecated and out of scope
      if (control.deprecated || outOfScope[control.domainID]) return false;

      const answer = answers[control.controlID];
      return !!answer && !answer.notApplicable && !!answer.unconfirmedControlUpdate;
    })
    .map((control) => control.controlID);
};

// Get sorted domains that have controls or scoping questions requiring attention.
// When reassessing it also returns domains that have unconfirmed controls.
// When not reassessing it also returns domains that have unanswered controls.
// Returns a list of domain objects.
const sortedIncompleteDomains: Getter<AssessmentState, RootState> = (
  state,
  getters,
  _rootState,
  rootGetters,
) => {
  const sortedDomains = rootGetters['framework/sortedDomains'] as Domain[];
  const domainsByID = rootGetters['framework/domainsByID'] as Record<string, Domain>;
  const outOfScope = getters.outOfScopeDeprecatedDoNotUseMe;

  // get domains from controls requiring attention
  const domainsFromControls = (getters.controlsRequiringAttention as string[]).map((controlID) => {
    const control = rootGetters['framework/controlsByID'][controlID];
    // oxlint-disable-next-line no-non-null-assertion
    return domainsByID[control.domainID]!;
  });

  // get domains from scoping questions requiring attention
  const domainsFromScopingQuestions = sortedDomains.filter(
    (domain) =>
      !domain.deprecated && domain.scopingQuestion && !state.scopingAnswers[domain.domainID],
  );

  // get non 100% answered domains
  const nonCompleteDomains = sortedDomains.filter(
    (domain) =>
      !domain.deprecated &&
      !outOfScope[domain.domainID] &&
      getters.percentageDomainAnswered(domain.domainID) < 100,
  );

  // combine and remove duplicates based on if we are reassessing or not
  const filteredDomains: Domain[] = [
    ...new Set([...domainsFromControls, ...domainsFromScopingQuestions, ...nonCompleteDomains]),
  ];

  // Sort filtered domains by letter
  filteredDomains.sort((a, b) => {
    if (a.letter < b.letter) {
      return -1;
    }
    if (a.letter > b.letter) {
      return 1;
    }

    return 0;
  });

  return filteredDomains;
};

// temporary getter until we have removed the assessment store (COR-809)
const outOfScopeDeprecatedDoNotUseMe: Getter<AssessmentState, RootState> = (
  state,
  _getters,
  rootState,
) =>
  Object.fromEntries(
    rootState.framework.domains.map((domain) => [
      domain.domainID,
      state.scopingAnswers?.[domain.domainID]?.answer === false,
    ]),
  );

export const getters = {
  numAnswersByDomain,
  percentageDomainAnswered,
  isScopingComplete,
  isAssessmentComplete,
  sortedControlAnswersDeprecated,
  controlsRequiringAttention,
  sortedIncompleteDomains,
  outOfScopeDeprecatedDoNotUseMe,
};
